import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react/components/shared/Icon";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { formatPaywallCurrencyAmount } from "@circle-react/helpers/paywalls/currencies";
import type { PaywallCheckoutPreview } from "@circle-react/types/Paywall";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";

const localeRoot = "paywall_checkout.coupon_badge";

interface CouponBadgeProp {
  setAddCouponClickedState: (param: boolean) => void;
}

const presentCoupon = (
  paywallCoupon: NonNullable<PaywallCheckoutPreview["paywall_coupon"]>,
  currency: NonNullable<PaywallCheckoutPreview["currency"]>,
): string =>
  t([localeRoot, paywallCoupon.duration], {
    amount:
      paywallCoupon.type === "PaywallCoupons::Percent"
        ? `${Number(paywallCoupon?.amount)}%`
        : formatPaywallCurrencyAmount(currency, Number(paywallCoupon?.amount), {
            amountInCents: false,
            includeCurrencyCode: false,
          }),
    count: paywallCoupon?.duration_in_months,
  });

export const CouponBadge = ({ setAddCouponClickedState }: CouponBadgeProp) => {
  const { watch } = useFormContext();
  const { couponPreviewMutation, clearCouponCode } =
    usePaywallCheckoutContext();

  const couponCode = watch("coupon_code");
  const onRemoveCoupon = () => {
    setAddCouponClickedState(false);
    clearCouponCode({ resetPreview: true });
  };

  const couponData = couponPreviewMutation?.data?.paywall_coupon;
  const currencyData = couponPreviewMutation?.data?.currency;

  if (couponPreviewMutation.isLoading) {
    return null;
  }

  return (
    <div
      className="bg-secondary flex w-fit max-w-full flex-row items-center justify-between gap-x-1 rounded-lg px-3 py-2.5 sm:w-fit md:gap-x-3 md:px-4"
      data-testid="checkout-form-coupon-code"
    >
      <Icon type="16-tags-topics" size={16} className="text-default shrink-0" />
      <div className="flex max-w-[80%] shrink flex-row items-center gap-x-2">
        <Typography.BodySm weight="medium" color="text-dark">
          {couponCode}
        </Typography.BodySm>

        {couponData && currencyData && (
          <TippyV2
            content={presentCoupon(couponData, currencyData)}
            placement="top"
            className="text-default truncate text-xs font-normal normal-case leading-4 tracking-normal"
          >
            {presentCoupon(couponData, currencyData)}
          </TippyV2>
        )}
      </div>

      <Button
        small
        className="text-default !p-0"
        type="button"
        variant="plain"
        onClick={onRemoveCoupon}
        data-testid="checkout-form-remove-coupon"
      >
        <span className="sr-only">
          {t("paywall_checkout.form.buttons.remove_coupon")}
        </span>
        <Icon type="24-close" size={24} className="text-default" />
      </Button>
    </div>
  );
};
